import {CircularProgress, FormControl, FormControlLabel, FormHelperText, Switch, SwitchProps} from "@mui/material";
import React, {useEffect, useMemo} from "react";
import {
    usePushSubscription,
    useSubscribePush,
    useUnsubscribePush
} from "~/notifications/hooks";
import {PortTheme} from "~/ui/theme";
import {makeStyles} from "@mui/styles";
import {useErrorHandler} from "~/components/ErrorHandling";
import {useCurrentUser} from "~/auth/hooks";

export type NotificationSwitchProps = Omit<SwitchProps, "checked" | "defaultChecked" | "defaultValue" | "value" | "onChange"> & {
    onChange?: (granted: boolean) => void
}


const useStyles = makeStyles<PortTheme, NotificationSwitchProps>(({ palette, shadows }) => ({
    labelRoot: {
        marginLeft: 0,
    }

}));


function useNotificationSwitchState() {
    const handleError = useErrorHandler();
    const {subscription, loading: loadingSubscription, error: loadingError} = usePushSubscription();
    const [subscribe, {loading: subscribing, error: subscribeError}] = useSubscribePush();
    const [unsubscribe, {loading: unsubscribing, error: unsubscribeError}] = useUnsubscribePush();
    const error = loadingError || subscribeError || unsubscribeError;
    const loading = loadingSubscription || subscribing || unsubscribing;
    const checked = (subscribing || subscription != null) && !unsubscribing;
    const forbidden = useMemo(() => {
        return [subscribeError, unsubscribeError].find(e => e?.message?.includes("permission denied")) != null;
    }, [subscribeError, unsubscribeError])

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error]);

    return {
        loading: loading,
        checked: checked,
        subscribe,
        unsubscribe,
        disabled: loading || error != null,
        forbidden: forbidden
    }
}


export const NotificationSwitch = (props: NotificationSwitchProps) => {
    const {onChange, ...other} = props;
    const classes = useStyles(props);
    const {loading, checked, forbidden, subscribe, unsubscribe, disabled} = useNotificationSwitchState()


    const handleChange: SwitchProps["onChange"] = (e) => {
        if (e.target.checked) {
            subscribe().catch(console.error);
        } else {
            unsubscribe().catch(console.error);
        }
    }

    return (
        <FormControl>
            <FormControlLabel
                disabled={disabled}
                control={
                    <Switch {...other} checked={checked} onChange={handleChange} />
                }
                label="Разрешить уведомления"
                classes={{root: classes.labelRoot}}
            />
            {forbidden && (
                <FormHelperText error>Запрещено настройками браузера</FormHelperText>
            )}
        </FormControl>
    )
}
